/**
 * Contact Form Enhancement
 * Handles floating labels, textarea auto-resize and loading states
 */

class ContactForm {
	constructor() {
		this.newsletterResetTimers = new WeakMap();
		this.init();
	}

	init() {
		this.setupTextareaAutoResize();
		this.setupFormSubmission();
		this.setupFloatingLabels();
		window.addEventListener("pageshow", () => {
			this.resetNewsletterFormStates();
		});
	}

	/**
	 * Auto-resize textarea based on content
	 */
	setupTextareaAutoResize() {
		const textareas = document.querySelectorAll(
			'textarea[id="ContactForm-body"]',
		);

		textareas.forEach((textarea) => {
			this.adjustTextareaHeight(textarea);
			textarea.addEventListener("input", () => {
				this.adjustTextareaHeight(textarea);
			});
			textarea.addEventListener("focus", () => {
				this.adjustTextareaHeight(textarea);
			});
		});
	}

	/**
	 * Adjust textarea height based on content
	 */
	adjustTextareaHeight(textarea) {
		textarea.style.height = "auto";

		const minHeight = 120;
		const maxHeight = 300;
		const newHeight = Math.max(
			minHeight,
			Math.min(maxHeight, textarea.scrollHeight),
		);

		textarea.style.height = newHeight + "px";
	}

	/**
	 * Setup floating label behavior for better browser compatibility
	 */
	setupFloatingLabels() {
		const inputs = document.querySelectorAll(
			"#ContactForm input, #ContactForm textarea",
		);

		inputs.forEach((input) => {
			this.updateLabelState(input);

			["input", "blur", "focus"].forEach((eventName) => {
				input.addEventListener(eventName, () => {
					this.updateLabelState(input);
				});
			});
		});
	}

	/**
	 * Update label state based on input value
	 */
	updateLabelState(input) {
		const fieldContainer = input.parentElement;
		const label = fieldContainer?.querySelector(`label[for="${input.id}"]`);

		if (!fieldContainer || !label) return;

		if (input.value.trim() !== "" || document.activeElement === input) {
			label.classList.add(
				"bx--translate-y-6",
				"bx-scale-75",
				"bx-text-theme-black",
			);
		} else {
			label.classList.remove(
				"bx--translate-y-6",
				"bx-scale-75",
				"bx-text-theme-black",
			);
		}
	}

	/**
	 * Enhanced form submission with loading states
	 */
	setupFormSubmission() {
		const contactForm = document.getElementById("ContactForm");
		const contactSubmitBtn = contactForm?.querySelector(
			'button[type="submit"]',
		);

		if (contactForm && contactSubmitBtn) {
			contactForm.addEventListener("submit", () => {
				if (!contactSubmitBtn.getAttribute("data-original-text")) {
					contactSubmitBtn.setAttribute(
						"data-original-text",
						contactSubmitBtn.textContent,
					);
				}

				contactSubmitBtn.disabled = true;
				contactSubmitBtn.textContent = "Sending...";
			});

			const errorContainer =
				contactForm.parentElement.querySelector('[class*="bg-red"]');
			if (errorContainer) {
				contactSubmitBtn.disabled = false;
				contactSubmitBtn.textContent =
					contactSubmitBtn.getAttribute("data-original-text") ||
					"Send";
			}
		}

		this.setupNewsletterFormSubmission();
	}

	/**
	 * Setup newsletter form submission with loading states
	 */
	setupNewsletterFormSubmission() {
		this.getNewsletterForms().forEach((form) => {
			const submitBtn = form.querySelector('button[type="submit"]');
			const submitText = submitBtn?.querySelector(".submit-text");
			const loadingSpinner = submitBtn?.querySelector(".loading-spinner");

			if (
				!submitBtn ||
				!submitText ||
				!loadingSpinner ||
				form.hasAttribute("data-newsletter-handled")
			) {
				return;
			}

			form.setAttribute("data-newsletter-handled", "true");
			form.addEventListener("submit", (event) => {
				if (event.defaultPrevented) return;

				// Only the first valid submit is allowed to continue to Shopify.
				if (form.dataset.newsletterSubmitting === "true") {
					event.preventDefault();
					return;
				}

				form.dataset.newsletterSubmitting = "true";
				submitBtn.setAttribute("aria-busy", "true");
				submitBtn.setAttribute("aria-disabled", "true");
				submitBtn.classList.add("bx-cursor-not-allowed");
				this.animateNewsletterLoading(submitText, loadingSpinner, true);

				// Keep the native submitter enabled during the first submit so the
				// browser can reliably send the request. Restore an interrupted request.
				const resetTimer = window.setTimeout(() => {
					this.resetNewsletterFormState(form);
				}, 12000);
				this.newsletterResetTimers.set(form, resetTimer);
			});
		});

		this.resetNewsletterFormStates();
	}

	/**
	 * Return forms that submit a customer email with newsletter tags.
	 */
	getNewsletterForms() {
		return Array.from(document.querySelectorAll("form")).filter((form) => {
			return Boolean(
				form.querySelector(
					'input[type="email"][name="contact[email]"]',
				) && form.querySelector('input[name="contact[tags]"]'),
			);
		});
	}

	/**
	 * Animate newsletter form loading state
	 */
	animateNewsletterLoading(submitText, loadingSpinner, isLoading) {
		submitText.style.opacity = isLoading ? "0" : "1";
		loadingSpinner.style.opacity = isLoading ? "1" : "0";
	}

	/**
	 * Reset all newsletter forms on page load or history restoration.
	 */
	resetNewsletterFormStates() {
		this.getNewsletterForms().forEach((form) => {
			this.resetNewsletterFormState(form);
		});
	}

	/**
	 * Restore one newsletter form after an interrupted request.
	 */
	resetNewsletterFormState(form) {
		const resetTimer = this.newsletterResetTimers.get(form);
		if (resetTimer) {
			window.clearTimeout(resetTimer);
			this.newsletterResetTimers.delete(form);
		}

		delete form.dataset.newsletterSubmitting;

		const submitBtn = form.querySelector('button[type="submit"]');
		const submitText = submitBtn?.querySelector(".submit-text");
		const loadingSpinner = submitBtn?.querySelector(".loading-spinner");

		if (!submitBtn || !submitText || !loadingSpinner) return;

		submitBtn.disabled = false;
		submitBtn.removeAttribute("aria-busy");
		submitBtn.removeAttribute("aria-disabled");
		submitBtn.classList.remove("bx-cursor-not-allowed");
		this.animateNewsletterLoading(submitText, loadingSpinner, false);
	}
}

function initContactForm() {
	new ContactForm();
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initContactForm, {
		once: true,
	});
} else {
	initContactForm();
}
